import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, MessageCircle, Mail, Loader2, CheckCircle, Send, ChevronDown } from 'lucide-react';
import { submitForm } from '../../lib/api';
import { getWhatsAppLink, getEmailLink } from '../../lib/utils';
import { useLanguage } from '../../context/LanguageContext';

export const IdeaSection = () => {
  const { t, language } = useLanguage();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      await submitForm({ form_type: 'idea', name: formData.name, email: formData.email, message: formData.message });
      setIsSuccess(true);
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => { setIsSuccess(false); setShowForm(false); }, 3000);
    } catch (err) {
      setError(t('form_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-[#0B1521]" data-testid="idea-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="w-16 h-16 rounded-2xl bg-[#4A90D9]/20 flex items-center justify-center mx-auto mb-6">
            <Lightbulb size={32} className="text-[#4A90D9]" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
            {t('idea_title_1')}{' '}
            <span className="text-[#4A90D9]">{t('idea_title_2')}</span>
          </h2>
          <p className="text-lg text-white/70 leading-relaxed mb-8 max-w-2xl mx-auto">
            {t('idea_subtitle')}
          </p>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a
              href={getWhatsAppLink(language === 'en' ? "Hello, I have a project idea to discuss." : "Bonjour, j'ai une idée de projet à discuter.")}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-full font-semibold hover:bg-[#20BD5A] transition-colors duration-200"
              data-testid="idea-whatsapp"
            >
              <MessageCircle size={20} />
              {t('whatsapp')}
            </a>
            <a
              href={getEmailLink(language === 'en' ? 'I have a project idea' : "J'ai une idée de projet", '')}
              className="flex items-center gap-2 px-6 py-3 bg-white/10 text-white rounded-full font-semibold hover:bg-white/20 transition-colors duration-200 border border-white/20"
              data-testid="idea-email"
            >
              <Mail size={20} />
              {t('email')}
            </a>
          </div>

          {/* Collapsible Form Trigger */}
          <button
            onClick={() => setShowForm(!showForm)}
            className={`inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all duration-300 ${
              showForm
                ? 'bg-white/10 text-white border border-white/20'
                : 'bg-[#4A90D9] text-white hover:bg-[#5BA0E9]'
            }`}
            data-testid="idea-toggle-form"
          >
            <Send size={18} />
            {language === 'fr' ? 'Partagez votre idée directement' : 'Share your idea directly'}
            <motion.div animate={{ rotate: showForm ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <ChevronDown size={18} />
            </motion.div>
          </button>
        </motion.div>

        {/* Collapsible Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="bg-[#152840] rounded-2xl p-8 border border-white/10 mt-8 max-w-2xl mx-auto">
                <h3 className="text-xl font-semibold text-white mb-6">{t('idea_form_title')}</h3>

                {isSuccess ? (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
                    <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                    <p className="text-white font-medium">{t('idea_success')}</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/80 text-sm mb-2 text-left">{t('form_name')} *</label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg input-dark" placeholder={t('form_name')} data-testid="idea-input-name" />
                      </div>
                      <div>
                        <label className="block text-white/80 text-sm mb-2 text-left">{t('form_email')} *</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg input-dark" placeholder={t('form_email')} data-testid="idea-input-email" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm mb-2 text-left">{t('form_describe_idea')} *</label>
                      <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} className="w-full px-4 py-3 rounded-lg input-dark resize-none" placeholder={t('form_describe_idea')} data-testid="idea-textarea" />
                    </div>
                    {error && <p className="text-red-400 text-sm">{error}</p>}
                    <button type="submit" disabled={isSubmitting} className="w-full py-3 bg-[#4A90D9] text-white rounded-full font-semibold hover:bg-[#5BA0E9] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="idea-submit-btn">
                      {isSubmitting ? <><Loader2 size={20} className="animate-spin" />{t('form_sending')}</> : t('idea_submit')}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default IdeaSection;
